// Spec 078 — a biblioteca de modelos 3D (Cadastro). Leitura para todos; enviar/calibrar/substituir/
// excluir só GESTOR (FR-007). Estado vazio vende o próximo passo; erro de leitura nunca vira
// "biblioteca vazia" (Spec 060).
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Notify } from "@/components/ui/notify";
import { useWebGLSupport } from "@/features/lines/scene/hooks/useWebGLSupport";
import { useAuthStore } from "@/stores/auth.store";
import { useModels3d } from "../hooks";
import { AssignMachinesDialog } from "./AssignMachinesDialog";
import { DeleteModelDialog } from "./DeleteModelDialog";
import { ModelCard } from "./ModelCard";
import { UploadDialog } from "./UploadDialog";
import type { Model3dSummary } from "@/types/models-3d.types";

type DialogState =
  | { kind: "upload" }
  | { kind: "replace"; model: Model3dSummary }
  | { kind: "assign"; model: Model3dSummary }
  | { kind: "delete"; model: Model3dSummary }
  | null;

export function Models3dLibrary(): React.ReactElement {
  const query = useModels3d();
  const navigate = useNavigate();
  const isGestor = useAuthStore((s) => s.user?.role === "GESTOR");
  const webgl = useWebGLSupport();
  const [dialog, setDialog] = React.useState<DialogState>(null);

  const uploadButton = isGestor ? (
    <Button onClick={() => setDialog({ kind: "upload" })}>
      <Upload className="h-4 w-4" aria-hidden />
      Enviar modelo
    </Button>
  ) : null;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-text-primary">Modelos 3D</h1>
          <p className="text-sm text-text-secondary">
            Um modelo por tipo de equipamento. A calibração (apoio, entrada e saída do produto) é
            feita uma vez e vale para toda máquina que o usa.
          </p>
        </div>
        {uploadButton}
      </header>

      {query.isError ? (
        <Notify.QueryError error={query.error} onRetry={() => void query.refetch()} />
      ) : query.isPending ? (
        <p className="text-sm text-text-tertiary">Carregando biblioteca…</p>
      ) : query.data.length === 0 ? (
        <Notify.Empty
          icon={<Box className="h-8 w-8" aria-hidden />}
          title="Nenhum modelo 3D ainda"
          description={
            isGestor
              ? "Envie o primeiro .glb, calibre onde o produto entra e sai, e associe às máquinas. Até lá, a cena 3D desenha blocos genéricos rotulados."
              : "Quando um gestor enviar modelos, eles aparecem aqui e na cena 3D das linhas."
          }
          action={uploadButton}
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {query.data.map((m) => (
            <ModelCard
              key={m.id}
              model={m}
              isGestor={isGestor}
              webgl={webgl}
              onReplace={(model) => setDialog({ kind: "replace", model })}
              onAssign={(model) => setDialog({ kind: "assign", model })}
              onDelete={(model) => setDialog({ kind: "delete", model })}
            />
          ))}
        </div>
      )}

      {dialog?.kind === "upload" ? (
        <UploadDialog
          onClose={() => setDialog(null)}
          onDone={(m) => {
            setDialog(null);
            navigate(`/modelos-3d/${m.id}/calibrar`);
          }}
        />
      ) : null}
      {dialog?.kind === "replace" ? (
        <UploadDialog
          replaceOf={dialog.model}
          onClose={() => setDialog(null)}
          onDone={(m) => {
            setDialog(null);
            navigate(`/modelos-3d/${m.id}/calibrar`);
          }}
        />
      ) : null}
      {dialog?.kind === "assign" ? (
        <AssignMachinesDialog model={dialog.model} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === "delete" ? (
        <DeleteModelDialog model={dialog.model} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
}
